/**
 * Developers — API keys + SDK snippets + webhook signing docs
 * ================================================================
 * The page that turns "we have an SDK story" into a copy-pasteable
 * 30-second integration. Everything a developer needs to wire CARTIE
 * into their stack lives here.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft, ArrowRight, Key, Code, Zap, ShieldCheck, Copy, Loader2, Plus, Trash2,
  CheckCircle2, AlertTriangle, Webhook,
} from 'lucide-react';
import { toast } from 'sonner';
import FAQSection from '../components/FAQSection';
import TrySDKLive from '../components/TrySDKLive';
import { faqsByPage } from '../data/faqs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const TAB_OVERVIEW = 'overview';
const TAB_KEYS = 'keys';
const TAB_TRACK = 'track';
const TAB_WEBHOOK = 'webhook';

const CodeBlock = ({ code, lang, label }) => {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success('Copied');
    } catch { toast.error('Copy failed'); }
  };
  return (
    <div className="rounded-md border border-white/10 bg-slate-950 overflow-hidden" data-testid={`code-${lang}`}>
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-white/5 text-[11px] text-slate-600 dark:text-slate-400">
        <span className="font-mono uppercase tracking-wider">{label || lang}</span>
        <Button size="sm" variant="ghost" onClick={copy} className="h-6 text-[11px] text-slate-600 dark:text-slate-400 hover:text-white">
          <Copy className="w-3 h-3 mr-1" /> Copy
        </Button>
      </div>
      <pre className="p-3 text-xs font-mono leading-relaxed text-slate-200 overflow-x-auto whitespace-pre">{code}</pre>
    </div>
  );
};

const Developers = () => {
  const [tab, setTab] = useState(TAB_OVERVIEW);
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [revealedKey, setRevealedKey] = useState(null);

  const apiBase = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const loadKeys = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api-keys`, auth());
      // Defensive dedupe — guards against backend or StrictMode quirks
      const seen = new Set();
      const unique = (data.keys || []).filter((k) => {
        if (!k?.id || seen.has(k.id)) return false;
        seen.add(k.id);
        return true;
      });
      setKeys(unique);
    } catch (e) { console.debug('keys fetch failed:', e?.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadKeys(); /* eslint-disable-line */ }, []);

  const createKey = async () => {
    if (!newKeyName.trim()) return toast.error('Give the key a name');
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/api-keys`,
        { name: newKeyName.trim(), scopes: ['events:write'] }, auth());
      setRevealedKey(data);
      setNewKeyName('');
      loadKeys();
    } catch (e) { toast.error('Could not create key', { description: e?.response?.data?.detail || e?.message }); }
    finally { setCreating(false); }
  };

  const revoke = async (id) => {
    try {
      await axios.delete(`${API}/api-keys/${id}`, auth());
      toast.success('Key revoked');
      loadKeys();
    } catch (e) { toast.error('Could not revoke', { description: e?.response?.data?.detail || e?.message }); }
  };

  const pythonSnippet = `# pip install httpx
import os, httpx
CARTIE_API_KEY = os.environ["CARTIE_API_KEY"]

def track_ai_cost(feature_slug, cost_usd, calls=1, model=None, customer_id=None):
    """Call this whenever your app makes an LLM/inference call."""
    httpx.post(
        "${apiBase}/ai-cost-events/track",
        json={"feature_slug": feature_slug, "cost_usd": cost_usd,
              "calls": calls, "model": model, "customer_id": customer_id},
        headers={"Authorization": f"Bearer {CARTIE_API_KEY}"},
        timeout=4.0,
    )

# Example
track_ai_cost("summarise", cost_usd=0.18, model="gpt-5.2", customer_id="acme-corp")`;

  const nodeSnippet = `// npm i undici
import { request } from 'undici';
const CARTIE_API_KEY = process.env.CARTIE_API_KEY;

export async function trackAiCost({ featureSlug, costUsd, calls = 1, model, customerId }) {
  await request("${apiBase}/ai-cost-events/track", {
    method: 'POST',
    headers: { 'authorization': \`Bearer \${CARTIE_API_KEY}\`, 'content-type': 'application/json' },
    body: JSON.stringify({
      feature_slug: featureSlug, cost_usd: costUsd, calls, model, customer_id: customerId,
    }),
  });
}

// Example
await trackAiCost({ featureSlug: 'summarise', costUsd: 0.18, model: 'gpt-5.2', customerId: 'acme-corp' });`;

  const curlSnippet = `curl -X POST ${apiBase}/ai-cost-events/track \\
  -H "Authorization: Bearer $CARTIE_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"feature_slug":"summarise","cost_usd":0.18,"calls":1,"model":"gpt-5.2","customer_id":"acme-corp"}'`;

  const webhookPython = `# Verify CARTIE webhooks (Flask example)
import hmac, hashlib, os, json
from flask import request, abort

WEBHOOK_SECRET = os.environ["CARTIE_WEBHOOK_SIGNING_SECRET"]

@app.post("/cartie-webhook")
def webhook():
    timestamp = request.headers.get("X-Cartie-Timestamp", "")
    signature = request.headers.get("X-Cartie-Signature", "").replace("sha256=", "")
    body = request.get_data()  # raw bytes!
    signed = f"{timestamp}.".encode() + body
    expected = hmac.new(WEBHOOK_SECRET.encode(), signed, hashlib.sha256).hexdigest()
    if not hmac.compare_digest(expected, signature):
        abort(401)
    payload = json.loads(body)
    if payload["action"] == "throttle":
        # Your throttle logic — return cached response, swap to cheaper model, etc.
        ...
    return ("", 200)`;

  const webhookNode = `// Verify CARTIE webhooks (Express example)
import crypto from 'node:crypto';
const SECRET = process.env.CARTIE_WEBHOOK_SIGNING_SECRET;

app.post('/cartie-webhook', express.raw({ type: 'application/json' }), (req, res) => {
  const ts = req.header('x-cartie-timestamp') || '';
  const sig = (req.header('x-cartie-signature') || '').replace('sha256=', '');
  const signed = Buffer.concat([Buffer.from(\`\${ts}.\`), req.body]);
  const expected = crypto.createHmac('sha256', SECRET).update(signed).digest('hex');
  if (!crypto.timingSafeEqual(Buffer.from(expected), Buffer.from(sig))) return res.status(401).end();
  const payload = JSON.parse(req.body.toString());
  if (payload.action === 'throttle') { /* throttle logic */ }
  res.status(200).end();
});`;

  // Show "Back to home" for unauthenticated visitors who landed here from /
  // the landing page; show "Back to dashboard" once they're logged in.
  const isAuthed = !!localStorage.getItem('token');

  return (
    <div className="container mx-auto p-4 sm:p-6 space-y-6 max-w-6xl" data-testid="developers-page">
      <div className="flex items-center gap-3">
        <Link to={isAuthed ? '/dashboard' : '/'} className="text-sm text-slate-700 dark:text-slate-300 hover:text-foreground inline-flex items-center gap-1.5" data-testid="back-link">
          <ArrowLeft className="h-4 w-4" /> {isAuthed ? 'Dashboard' : 'Back to home'}
        </Link>
        {!isAuthed && (
          <>
            <span className="text-slate-700 dark:text-slate-300/40">·</span>
            <Link to="/pricing" className="text-sm text-slate-700 dark:text-slate-300 hover:text-emerald-500 transition-colors" data-testid="dev-pricing-link">Pricing</Link>
            <span className="text-slate-700 dark:text-slate-300/40">·</span>
            <Link to="/login" className="text-sm text-emerald-500 hover:text-emerald-400 font-medium transition-colors" data-testid="dev-signin-link">Sign in →</Link>
          </>
        )}
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
            <Code className="w-7 h-7 text-emerald-500" />
            Developers
          </h1>
          <p className="text-sm text-slate-700 dark:text-slate-300 mt-1 max-w-2xl">
            API keys, SDK snippets, and webhook signing docs. Wire CARTIE into your stack in &lt;5 minutes.
          </p>
        </div>
      </div>

      {/* Tab strip */}
      <div className="flex gap-1.5 border-b border-border overflow-x-auto" data-testid="tabs">
        {[
          { key: TAB_OVERVIEW, label: 'Overview' },
          { key: TAB_KEYS,     label: 'API Keys' },
          { key: TAB_TRACK,    label: 'Track AI cost' },
          { key: TAB_WEBHOOK,  label: 'Verify webhooks' },
        ].map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            data-testid={`tab-${t.key}`}
            className={`px-3 py-1.5 text-sm rounded-t-md transition-all ${
              tab === t.key
                ? 'border-b-2 border-emerald-500 text-emerald-500 font-semibold'
                : 'text-slate-700 dark:text-slate-300 hover:text-foreground'
            }`}>{t.label}</button>
        ))}
      </div>

      {/* Overview */}
      {tab === TAB_OVERVIEW && (
        <div className="space-y-6" data-testid="overview-tab">
          {/* The interactive 60-second sandbox — strongest conversion lever */}
          <TrySDKLive />

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="overview-grid">
            {[
              { icon: Key,        title: '1. Get an API key',         desc: 'Create one on the API Keys tab. The plaintext is shown once — copy it and store as CARTIE_API_KEY.' },
              { icon: Zap,        title: '2. Track every AI call',    desc: 'POST a tiny event whenever you call OpenAI / Anthropic / Gemini / Groq / Mistral / Bedrock. Token Budgets and AI Profitability light up automatically.' },
              { icon: ShieldCheck,title: '3. Verify webhook signatures',desc: 'When CARTIE pings your throttle endpoint, verify the HMAC-SHA256 signature so you know it\'s really us.' },
            ].map((s, i) => {
              const Icon = s.icon;
              return (
                <Card key={i} className="hover:border-emerald-500/30 transition-colors">
                  <CardContent className="p-5">
                    <Icon className="w-7 h-7 text-emerald-500 mb-3" />
                    <p className="font-bold text-sm mb-1">{s.title}</p>
                    <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed">{s.desc}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* API Keys */}
      {tab === TAB_KEYS && (
        <div className="space-y-4" data-testid="keys-tab">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Plus className="w-4 h-4 text-emerald-500" /> Create a new key
              </CardTitle>
              <CardDescription>The plaintext key is shown once, only on creation.</CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col sm:flex-row gap-2">
              <Input value={newKeyName} onChange={(e) => setNewKeyName(e.target.value)}
                placeholder="e.g. Production server, CI runner, local dev"
                data-testid="new-key-name" />
              <Button onClick={createKey} disabled={creating}
                className="bg-emerald-500 hover:bg-emerald-400 text-slate-950"
                data-testid="create-key-btn">
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Create key</>}
              </Button>
            </CardContent>
          </Card>

          {revealedKey && (
            <Card className="border-amber-500/40 bg-amber-500/5" data-testid="revealed-key">
              <CardContent className="p-5 space-y-3">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm font-semibold text-amber-500">
                    Copy this now — it won't be shown again.
                  </p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-widest text-slate-700 dark:text-slate-300 mb-1">API key</p>
                  <CodeBlock code={revealedKey.plaintext_key} lang="key" label="API KEY · paste into CARTIE_API_KEY env var" />
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-widest text-slate-700 dark:text-slate-300 mb-1">Webhook signing secret</p>
                  <CodeBlock code={revealedKey.webhook_signing_secret} lang="secret" label="WEBHOOK SIGNING SECRET · paste into CARTIE_WEBHOOK_SIGNING_SECRET" />
                </div>
                <Button variant="ghost" size="sm" onClick={() => setRevealedKey(null)}
                  className="text-slate-700 dark:text-slate-300" data-testid="dismiss-revealed">
                  I saved them — dismiss
                </Button>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader><CardTitle className="text-base">Active keys</CardTitle></CardHeader>
            <CardContent className="space-y-2" data-testid="keys-list">
              {loading ? (
                <div className="text-center py-6 text-slate-700 dark:text-slate-300">
                  <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…
                </div>
              ) : keys.length === 0 ? (
                <p className="text-sm text-slate-700 dark:text-slate-300 py-4">No keys yet — create one above.</p>
              ) : keys.map((k) => (
                <div key={k.id} className="flex items-center gap-3 p-3 rounded-md border border-border" data-testid={`key-row-${k.id}`}>
                  <Key className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm">{k.name}</span>
                      {k.revoked_at && <Badge variant="outline" className="text-[11px] text-rose-500 border-rose-500/30">REVOKED</Badge>}
                    </div>
                    <p className="text-[11px] text-slate-700 dark:text-slate-300 font-mono">
                      cartie_{k.prefix}_••••••••••••••••
                      {k.last_used_at && <span className="ml-2">· last used {new Date(k.last_used_at).toLocaleDateString()}</span>}
                      {!k.last_used_at && <span className="ml-2 text-amber-500/80">· never used</span>}
                    </p>
                  </div>
                  {!k.revoked_at && (
                    <Button size="sm" variant="ghost"
                      className="text-slate-700 dark:text-slate-300 hover:text-rose-500 h-8 w-8 p-0"
                      onClick={() => revoke(k.id)}
                      data-testid={`revoke-${k.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Track */}
      {tab === TAB_TRACK && (
        <div className="space-y-4" data-testid="track-tab">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Zap className="w-4 h-4 text-emerald-500" /> Track an AI cost event
              </CardTitle>
              <CardDescription>
                One POST per inference call. The endpoint accepts JWT (dashboard) or API key (server-to-server) — same shape, same response.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <CodeBlock code={pythonSnippet} lang="python" label="PYTHON" />
              <CodeBlock code={nodeSnippet}   lang="javascript" label="NODE.JS" />
              <CodeBlock code={curlSnippet}   lang="bash" label="CURL" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">What you get back</CardTitle></CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p>The event flows into <code className="text-emerald-500">ai_cost_events</code> and is reflected in:</p>
              <ul className="text-sm text-slate-700 dark:text-slate-300 pl-5 list-disc space-y-0.5">
                <li><Link to="/ai-feature-profitability" className="text-emerald-500 hover:underline">AI Feature Profitability</Link> — accurate per-feature cost vs revenue, real-time</li>
                <li>Token Budgets <code>/check</code> uses real consumption (not the projection)</li>
                <li>Daily AI Brief detects feature margin shifts based on event data</li>
                <li>Customer P&amp;L attribution by <code>customer_id</code></li>
              </ul>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Verify webhooks */}
      {tab === TAB_WEBHOOK && (
        <div className="space-y-4" data-testid="webhook-tab">
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Webhook className="w-4 h-4 text-emerald-500" /> Verify webhook signatures
              </CardTitle>
              <CardDescription>
                When a Token Budget hits 100% and you've set <code>hard_cap_action=throttle</code>, CARTIE POSTs to your URL. Always verify the signature — never trust a webhook unverified.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                {[
                  ['Header', 'X-Cartie-Signature', 'sha256=<hex>'],
                  ['Header', 'X-Cartie-Timestamp', 'unix epoch seconds'],
                  ['Header', 'X-Cartie-Event',     'ai_token_budget.exceeded'],
                ].map(([type, name, val]) => (
                  <div key={name} className="p-3 rounded-md border border-border">
                    <p className="text-[11px] uppercase tracking-widest text-slate-700 dark:text-slate-300">{type}</p>
                    <code className="text-emerald-500 text-xs">{name}</code>
                    <p className="text-[11px] text-slate-700 dark:text-slate-300 mt-0.5">{val}</p>
                  </div>
                ))}
              </div>
              <div className="p-3 rounded-md bg-emerald-500/5 border border-emerald-500/20 text-xs">
                <p className="font-semibold mb-1.5 flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> Signature recipe
                </p>
                <code className="text-slate-700 dark:text-slate-300 block">sha256(secret, "&lt;timestamp&gt;.&lt;raw_body_bytes&gt;")</code>
              </div>
              <CodeBlock code={webhookPython} lang="python" label="PYTHON · FLASK" />
              <CodeBlock code={webhookNode}   lang="javascript" label="NODE.JS · EXPRESS" />
            </CardContent>
          </Card>
        </div>
      )}

      {/* Developer FAQ — always visible at the bottom */}
      <div className="pt-6 mt-6 border-t border-white/10" data-testid="developers-faq">
        <FAQSection
          title="Developer FAQ"
          subtitle="Quick answers to the questions every dev asks before wiring up."
          items={faqsByPage.developers}
          testIdPrefix="dev-faq"
          variant="card"
        />
      </div>

      {/* Forward navigation — clear next-step CTAs (Feb 24 2026) */}
      <div className="mt-12 rounded-2xl border border-emerald-500/30 bg-slate-900/60 p-8 shadow-md shadow-emerald-500/10" data-testid="dev-next-steps">
        <h3 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">Ready to wire it up?</h3>
        <p className="text-slate-700 dark:text-slate-300 mt-2 mb-6 max-w-2xl">
          Start a 14-day free pilot — no card. Use these snippets against your live cloud and LLM spend.
        </p>
        <div className="flex flex-col sm:flex-row flex-wrap gap-3">
          <Link to="/pricing">
            <Button size="lg" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-6 h-12 w-full sm:w-auto shadow-md shadow-emerald-500/20 transition-all hover:-translate-y-0.5" data-testid="dev-cta-pilot">
              Start free pilot
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
          <Link to="/policy-playground">
            <Button size="lg" variant="outline" className="border-emerald-400/40 bg-emerald-500/5 text-emerald-200 hover:bg-emerald-500/15 hover:text-white px-6 h-12 w-full sm:w-auto" data-testid="dev-cta-playground">
              Try Policy Playground
            </Button>
          </Link>
          <Link to="/">
            <Button size="lg" variant="outline" className="border-white/10 bg-transparent text-slate-600 dark:text-slate-400 hover:bg-white/5 hover:text-white px-6 h-12 w-full sm:w-auto" data-testid="dev-cta-home">
              ← Back to home
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Developers;
